/**
 * Utility functions for handling URLs safely
 */

const S3_BASE_URL = 'https://mercadoagentes.s3.amazonaws.com/';

/**
 * Safely extracts URL from various input formats
 * @param {string|object|null|undefined} input - Input that might contain a URL
 * @returns {string} - Extracted URL or empty string
 */
export const extractUrl = (input) => {
  if (!input) return '';
  
  if (typeof input === 'string') {
    return input;
  }
  
  if (typeof input === 'object') {
    // Try common URL properties
    return input.url || input.image || input.src || input.href || '';
  }
  
  return '';
};

/**
 * Converts a potentially relative URL to absolute URL
 * @param {string} url - URL that might be relative
 * @returns {string} - Absolute URL
 */
export const makeAbsoluteUrl = (url) => {
  const urlString = String(url || '');
  
  if (!urlString) return '';
  
  // Already absolute URL
  if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
    return urlString;
  }
  
  // Data URL (base64 images)
  if (urlString.startsWith('data:')) {
    return urlString;
  }
  
  // Blob URL (uploaded files)
  if (urlString.startsWith('blob:')) {
    return urlString;
  }
  
  // Relative URL - make it absolute with S3 base
  return `${S3_BASE_URL}${urlString}`;
};

/**
 * Processes image item from API response safely
 * @param {string|object} imageItem - Image item from API
 * @param {number} index - Index for unique ID generation
 * @returns {object} - Processed image object
 */
export const processImageItem = (imageItem, index) => {
  const extractedUrl = extractUrl(imageItem);
  const absoluteUrl = makeAbsoluteUrl(extractedUrl);
  
  return {
    id: `gallery_${index}_${Date.now()}`,
    url: absoluteUrl,
    name: `Imagem ${index + 1}`,
    isNew: false,
    isExisting: true,
  };
};

/**
 * Validates if a string is a valid URL
 * @param {string} url - URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
export const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Sanitizes URL for safe usage
 * @param {string} url - URL to sanitize
 * @returns {string} - Sanitized URL
 */
export const sanitizeUrl = (url) => {
  const urlString = String(url || '').trim();
  
  if (!urlString) return '';
  
  // Remove potentially dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = urlString.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '';
    }
  }
  
  return urlString;
};